import React, { useRef, useState, useEffect, useCallback } from "react";
import { Modal, Tabs, Button, Radio, Space, message, Typography, Checkbox } from "antd";
import {
  EditOutlined,
  UploadOutlined,
  FontSizeOutlined,
  ClearOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const HANDWRITING_FONTS = [
  { id: "font1", name: "Chữ ký Mềm mại", style: "italic 38px 'Brush Script MT', cursive, sans-serif" },
  { id: "font2", name: "Chữ ký Bay bổng", style: "italic 36px 'Segoe Script', cursive, sans-serif" },
  { id: "font3", name: "Chữ ký Mạnh mẽ", style: "italic bold 34px 'Lucida Handwriting', cursive, sans-serif" },
  { id: "font4", name: "Chữ ký Hiện đại", style: "italic 32px 'Great Vibes', 'Palatino Linotype', serif" },
];

export default function SignatureModal({
  open,
  onCancel,
  onConfirm,
  title = "Ký chữ ký điện tử",
  signerName = "",
  signerRole = "Người lao động",
}) {
  const [activeTab, setActiveTab] = useState("draw");
  const [inkColor, setInkColor] = useState("#0038a8"); // Xanh mực công văn
  const [hasDrawn, setHasDrawn] = useState(false);
  const [agreed, setAgreed] = useState(true);

  // Type tab state
  const [typedName, setTypedName] = useState(signerName || "");
  const [selectedFont, setSelectedFont] = useState(HANDWRITING_FONTS[0].id);

  // Upload tab state
  const [uploadedImage, setUploadedImage] = useState(null);

  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const lastMidPointRef = useRef({ x: 0, y: 0 });
  const pointsCountRef = useRef(0);

  // Reset or setup canvas when opening
  useEffect(() => {
    if (open) {
      setHasDrawn(false);
      setTypedName(signerName || "");
      setUploadedImage(null);
      setTimeout(initCanvas, 150);
    }
  }, [open, signerName]);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.8;
    ctx.strokeStyle = inkColor;
    ctx.setLineDash([]);
  }, [inkColor]);

  // Handle ink color change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = inkColor;
      ctx.setLineDash([]);
    }
  }, [inkColor]);

  // Coordinate helper
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    const point = getCoordinates(e);
    lastPointRef.current = point;
    lastMidPointRef.current = point;
    pointsCountRef.current = 1;

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.8;
      ctx.strokeStyle = inkColor;
      ctx.setLineDash([]);
    }
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const currentPoint = getCoordinates(e);

    // Tính điểm giữa (midpoint) giữa điểm trước đó và điểm hiện tại
    const midPoint = {
      x: (lastPointRef.current.x + currentPoint.x) / 2,
      y: (lastPointRef.current.y + currentPoint.y) / 2,
    };

    ctx.beginPath();
    // Bắt đầu từ midpoint trước để tạo nét liền mạch hoàn toàn, không đứt đoạn
    ctx.moveTo(lastMidPointRef.current.x, lastMidPointRef.current.y);
    ctx.quadraticCurveTo(
      lastPointRef.current.x,
      lastPointRef.current.y,
      midPoint.x,
      midPoint.y
    );
    ctx.stroke();

    lastPointRef.current = currentPoint;
    lastMidPointRef.current = midPoint;
    pointsCountRef.current += 1;
    setHasDrawn(true);
  };

  const stopDrawing = (e) => {
    if (isDrawingRef.current) {
      if (e && e.preventDefault) e.preventDefault();
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (pointsCountRef.current === 1) {
          // Trường hợp click chuột/chạm 1 điểm (như dấu chấm, dấu chữ)
          ctx.beginPath();
          ctx.arc(lastPointRef.current.x, lastPointRef.current.y, 1.4, 0, Math.PI * 2);
          ctx.fillStyle = inkColor;
          ctx.fill();
          setHasDrawn(true);
        } else {
          // Vẽ đoạn kết thúc từ midpoint cuối tới điểm nhả chuột
          ctx.beginPath();
          ctx.moveTo(lastMidPointRef.current.x, lastMidPointRef.current.y);
          ctx.lineTo(lastPointRef.current.x, lastPointRef.current.y);
          ctx.stroke();
        }
      }
      isDrawingRef.current = false;
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasDrawn(false);
      pointsCountRef.current = 0;
    }
  };

  // Convert Typed Name to Canvas
  const generateTypedSignatureCanvas = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 220;
    const ctx = canvas.getContext("2d");
    const fontObj = HANDWRITING_FONTS.find((f) => f.id === selectedFont) || HANDWRITING_FONTS[0];

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = inkColor;
    ctx.font = fontObj.style;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const displayName = typedName || signerName || "Chữ ký";
    ctx.fillText(displayName, canvas.width / 2, canvas.height / 2 - 10);

    // Gạch chân trang trí chữ ký nghệ thuật
    ctx.strokeStyle = inkColor;
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    const textWidth = ctx.measureText(displayName).width;
    const startX = canvas.width / 2 - textWidth / 2 - 10;
    const endX = canvas.width / 2 + textWidth / 2 + 15;
    const lineY = canvas.height / 2 + 25;
    ctx.moveTo(startX, lineY);
    ctx.quadraticCurveTo(canvas.width / 2, lineY + 12, endX, lineY - 6);
    ctx.stroke();

    return canvas.toDataURL("image/png");
  };

  // Handle submit signature
  const handleConfirm = () => {
    if (!agreed) {
      message.warning("Vui lòng tích chọn cam kết trước khi xác nhận chữ ký!");
      return;
    }

    let signatureDataUrl = null;

    if (activeTab === "draw") {
      if (!hasDrawn) {
        message.warning("Vui lòng vẽ chữ ký của bạn trước khi xác nhận!");
        return;
      }
      signatureDataUrl = canvasRef.current.toDataURL("image/png");
    } else if (activeTab === "type") {
      if (!typedName.trim()) {
        message.warning("Vui lòng nhập họ tên để tạo chữ ký!");
        return;
      }
      signatureDataUrl = generateTypedSignatureCanvas();
    } else if (activeTab === "upload") {
      if (!uploadedImage) {
        message.warning("Vui lòng tải ảnh chữ ký lên!");
        return;
      }
      signatureDataUrl = uploadedImage;
    }

    if (signatureDataUrl) {
      onConfirm(signatureDataUrl);
    }
  };

  const handleUploadImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.error("Vui lòng chọn file hình ảnh (.png, .jpg, .jpeg)!");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target.result);
      setHasDrawn(true);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <EditOutlined style={{ color: "#1677ff", fontSize: 20 }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{title}</div>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 400 }}>
              Ký danh: <b>{signerName || "Không xác định"}</b> ({signerRole})
            </div>
          </div>
        </div>
      }
      width={640}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Hủy bỏ
        </Button>,
        <Button
          key="confirm"
          type="primary"
          icon={<CheckCircleOutlined />}
          style={{ background: "#1677ff", fontWeight: 600 }}
          onClick={handleConfirm}
        >
          Xác nhận chữ ký này
        </Button>,
      ]}
    >
      <div style={{ padding: "8px 0" }}>
        {/* Lựa chọn màu mực */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Space>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Màu mực chữ ký:</span>
            <Radio.Group value={inkColor} onChange={(e) => setInkColor(e.target.value)} size="small">
              <Radio.Button value="#0038a8">
                <span style={{ color: "#0038a8", fontWeight: 700 }}>● Mực Xanh (Khuyên dùng)</span>
              </Radio.Button>
              <Radio.Button value="#111111">
                <span style={{ color: "#111111", fontWeight: 700 }}>● Mực Đen</span>
              </Radio.Button>
            </Radio.Group>
          </Space>

          {activeTab === "draw" && (
            <Button size="small" icon={<ClearOutlined />} onClick={clearCanvas} danger>
              Xóa vẽ lại
            </Button>
          )}
        </div>

        {/* Tab chuyển đổi hình thức ký */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "draw",
              label: (
                <span>
                  <EditOutlined /> Vẽ tay
                </span>
              ),
              children: (
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      border: "2px dashed #94a3b8",
                      borderRadius: 10,
                      background: "#fafafa",
                      cursor: "crosshair",
                      boxShadow: "inset 0 2px 6px rgba(0,0,0,0.05)",
                      overflow: "hidden",
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      width={580}
                      height={200}
                      style={{
                        display: "block",
                        width: "100%",
                        height: 200,
                        touchAction: "none",
                      }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  {!hasDrawn && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        pointerEvents: "none",
                        color: "#94a3b8",
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    >
                      ✍ Dùng chuột hoặc ngón tay để vẽ chữ ký vào đây
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: "type",
              label: (
                <span>
                  <FontSizeOutlined /> Gõ tên tạo mẫu
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
                      Nhập họ tên đầy đủ:
                    </label>
                    <input
                      type="text"
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Ví dụ: Nguyễn Văn A"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                        fontSize: 14,
                        outline: "none",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
                      Chọn kiểu chữ viết tay:
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {HANDWRITING_FONTS.map((font) => (
                        <div
                          key={font.id}
                          onClick={() => setSelectedFont(font.id)}
                          style={{
                            padding: "12px",
                            border: `2px solid ${selectedFont === font.id ? "#1677ff" : "#e2e8f0"}`,
                            borderRadius: 8,
                            background: selectedFont === font.id ? "#f0f7ff" : "#fff",
                            cursor: "pointer",
                            textAlign: "center",
                          }}
                        >
                          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>{font.name}</div>
                          <div
                            style={{
                              font: font.style,
                              color: inkColor,
                              fontSize: 22,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {typedName || signerName || "Xem trước chữ ký"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: "upload",
              label: (
                <span>
                  <UploadOutlined /> Tải ảnh chữ ký
                </span>
              ),
              children: (
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <label
                    style={{
                      display: "inline-block",
                      padding: "10px 20px",
                      background: "#f1f5f9",
                      border: "1px dashed #94a3b8",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    📁 Chọn file ảnh chữ ký (.png, .jpg)
                    <input type="file" accept="image/*" hidden onChange={handleUploadImage} />
                  </label>

                  {uploadedImage && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Xem trước ảnh chữ ký:</div>
                      <div
                        style={{
                          display: "inline-block",
                          padding: 10,
                          border: "1px solid #cbd5e1",
                          borderRadius: 8,
                          background: "#fff",
                        }}
                      >
                        <img src={uploadedImage} alt="Uploaded signature" style={{ maxHeight: 110, maxWidth: 360 }} />
                      </div>
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />

        {/* Cam kết pháp lý */}
        <div
          style={{
            marginTop: 16,
            padding: "10px 14px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
          }}
        >
          <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
            <span style={{ fontSize: 12, color: "#334155" }}>
              Tôi cam kết đây là chữ ký hợp pháp của tôi, đồng ý sử dụng chữ ký điện tử để giao kết hợp đồng này theo
              quy định của Luật Giao dịch điện tử.
            </span>
          </Checkbox>
        </div>
      </div>
    </Modal>
  );
}
